import React, { useRef, useCallback } from 'react'
import { useIsMobile, useIsXs } from 'hooks/responsive'
import { MetaData } from 'components/MetaData'
import { useProductListPage, useCategoryCmsBlock } from 'lib/modules/catalog'
import { EmptyContainer, Spinner, FadeIn, FlexWrapper } from 'lib/ui'
import { Category } from 'lib/types'
import { ProductList } from 'components/ProductList'
import { Breadcrumbs } from 'components/Breadcrumbs'
import { LayeredNav } from 'components/LayeredNav'
import { LayeredControls } from 'components/LayeredControls'
import {
  Wrapper,
  Container,
  Sidebar,
  ContentWrapper,
  DescriptionWrapper,
  CategoryBlock,
  BrandHero,
  BreadcrumbsWrapper
} from './styled'

interface Props {
  title: string
  breadcrumbTitle?: string
  categoryId?: number
  loadingState?: boolean
  category: Category
}

export const ProductListLayout = ({
  categoryId,
  category,
  title,
  breadcrumbTitle,
  loadingState
}: Props) => {
  const categoryTopRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile()
  const isTablet = useIsXs()

  const {
    description: categoryDescription,
    parentCategory
  } = category ?? {}

  const isBrand = parentCategory?.urlPath === 'shop-by-brand'

  const scrollTop = () => {
    window.scroll({ top: (categoryTopRef.current?.offsetTop ?? 0 - 20), left: 0, behavior: 'smooth' })
  }

  const { isError, isFetching, isLoading, products, setFilters } = useProductListPage({
    categoryId,
    enableGTM: true,
    withProducts: true
  })

  const { cmsBlock } = useCategoryCmsBlock(categoryId ?? 0)

  const handleClearFilters = useCallback(() => {
    scrollTop()
    setFilters({})
  }, [setFilters])

  return (
    <Wrapper
      isBrand={isBrand}
    >
      <FadeIn>
        <MetaData
          type={categoryId ? 'PLP' : undefined}
          title={categoryId ? undefined : title}
        />

        {cmsBlock && isBrand &&(
          <BrandHero>
            <div
              dangerouslySetInnerHTML={{
                __html: cmsBlock?.content
              }}
            />
          </BrandHero>
        )}

        <Container>
          {!isMobile && !isBrand && (
            <BreadcrumbsWrapper>
              <Breadcrumbs title={breadcrumbTitle} />
            </BreadcrumbsWrapper>
          )}
          {cmsBlock && !isBrand &&(
            <CategoryBlock>
              <div
                dangerouslySetInnerHTML={{
                  __html: cmsBlock?.content
                }}
              />
            </CategoryBlock>
          )}
          {isBrand && (
            <h1>Shop {title} products.</h1>
          )}

          <div
            ref={categoryTopRef}
          />

          {(isLoading || loadingState) ? (
            <EmptyContainer>
              <Spinner />
            </EmptyContainer>
          ) : (
            <div className={`content-wrapper ${isBrand && 'is-brand'}`}>
              <FlexWrapper>
                {!isTablet && (
                  <Sidebar>
                    <LayeredNav
                      categoryId={categoryId}
                      onFilterChange={scrollTop}
                      onClearFiltersPress={handleClearFilters}
                    />
                  </Sidebar>
                )}
                <ContentWrapper>
                  <div className='toolbar'>
                    <LayeredControls
                      isDraft={false}
                      isMulti={false}
                      categoryId={categoryId}
                      showPagination={false}
                      title={title}
                      isBrand={isBrand}
                    />
                  </div>

                  <ProductList
                    listPage={categoryId ? 'Category Page' : 'Search Page'}
                    isError={isError}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    products={products}
                    clearFiltersHandler={() => setFilters({})}
                  />

                  <div className='toolbar__bottom'>
                    <LayeredControls
                      categoryId={categoryId}
                      showSorter={false}
                      showTotalCount={false}
                      onChange={scrollTop}
                      showLayeredNavAction={false}
                    />
                  </div>

                  <DescriptionWrapper>
                    {categoryDescription}
                  </DescriptionWrapper>
                </ContentWrapper>
              </FlexWrapper>

            </div>
          )}
        </Container>
      </FadeIn>
    </Wrapper>
  )
}
